import { dayjs, defineResource, thaiIdValid } from '@tnn/core'
import { z } from 'zod'
import { partyTypes } from '../party'
import { type PayingParty, payerDefaults } from '../tms/payer'
import { juristicTaxIdSchema, type ReceiptLine } from '../tms/receipt'
import type { RefundKind, RefundStatus } from '../tms/refund'
import { type PaySide, paySides } from '../tms/waybill'

/**
 * accounting.customerReceipt (ใบเสร็จใส่ชื่อลูกค้า): a receipt in the customer's name in place of a counter receipt
 * (tms.receipt) that went without it — a company that wants one in its name for its books, often after the money was
 * taken. The same money as the counter receipt (`customerReceiptFigures`), so nothing new is booked: no cash, no bills
 * paid, nothing counted twice. Dated the day it is issued (`issueDate`, numbered in that month: `RC6911-0001`), it names
 * the day the money came (`paidOn`) and the receipt it replaces, which the customer hands back (`recalled`, or why
 * not). In the name of whoever paid, seldom as the bill wrote it (a company's registered name, kept on the contact as
 * `registeredName`), why noted if need be (`nameReason`); the list shows both names. A company has its tax ID and branch printed; a person their name and address only, never an ID card number.
 *
 * One issued at a time for a counter receipt: issued again, the old one is cancelled first (then amended). While one
 * stands, the counter receipt keeps its money and payer and is not cancelled, and its refunds wait.
 *
 * Accounting issues it, or a company that paid issues it itself from the bill's tracking page within
 * `SELF_RECEIPT_DAYS` (`selfReceiptState`, `selfReceiptInput`; `selfIssued`, the counter receipt not handed back).
 * Whoever took the money signs for it: printed under ผู้รับเงิน as the counter receipt has them (`receivedBy`).
 *
 * Freight is exempt from VAT, so it is a receipt, never a tax invoice.
 */
export const customerReceiptStatuses = ['draft', 'issued', 'cancelled'] as const
export type CustomerReceiptStatus = (typeof customerReceiptStatuses)[number]

/** What it says the customer paid for one bill: the counter receipt's line, less a refund paid of it */
export const customerReceiptLineSchema = z.object({
  waybillId: z.string().min(1),
  waybillNumber: z.string().nullish(),
  side: z.enum(paySides),
  amount: z.number().min(0),
})
export type CustomerReceiptLine = z.infer<typeof customerReceiptLineSchema>

const text = (max: number) => z.string().trim().max(max).nullish()

export const customerReceipt = defineResource({
  app: 'accounting',
  name: 'customerReceipt',
  path: 'customer-receipts',
  schema: z.object({
    /** The counter receipt it replaces */
    receiptId: z.string().min(1),
    /** Filled from who paid when the draft is made (`customerReceiptDefaults`); needed to issue it */
    partyType: z.enum(partyTypes).nullish(),
    name: text(200),
    /** Why the name is not the counter receipt's payer, if it needs saying */
    nameReason: text(300),
    /** A company's (13 digits starting with 0): a person's ID card number is never asked for */
    taxId: juristicTaxIdSchema.nullish(),
    /** A company's: 00000 = head office, otherwise its branch number */
    branch: text(10),
    address: text(500),
    subdistrict: text(100),
    district: text(100),
    province: text(100),
    postcode: text(10),
    /** The customer handed back the counter receipt… */
    recalled: z.boolean().default(false),
    /** …or why they could not */
    notRecalledNote: text(300),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    /** The day it is issued (server-set) */
    issueDate: { readOnly: true, type: 'date', filter: true, list: { width: 130, priority: 1 } },
    receiptId: {
      type: 'ref',
      ref: { resource: 'tms.receipt', where: { status: 'issued' } },
      search: true,
      filter: true,
      list: { width: 150, priority: 2 },
      form: { span: 6 },
    },
    /** The day the customer paid: the counter receipt's (server-set) */
    paidOn: { readOnly: true, type: 'date', filter: true, list: { width: 130, priority: 2 } },
    /** The counter receipt's branch (server-set) */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    /** Who paid, as the counter receipt has it (server-set) */
    payerName: { readOnly: true, search: true, list: { priority: 2 } },
    partyType: { label: 'party.type', optionLabels: 'party', filter: true, list: false, form: { span: 4, section: 'customer' } },
    name: { search: true, list: { priority: 1 }, form: { span: 8, section: 'customer' } },
    nameReason: { list: false, form: { span: 12, section: 'customer' } },
    // Drawn by the app only for a company
    taxId: { type: 'taxId', search: true, list: { priority: 3, hidden: true }, form: { span: 6, section: 'customer', custom: true } },
    branch: { list: false, form: { span: 6, section: 'customer', custom: true } },
    address: { type: 'textarea', list: false, form: { span: 12, section: 'address' } },
    province: { list: false, form: { span: 12, section: 'address', custom: true } },
    district: { list: false, form: { hidden: true } },
    subdistrict: { list: false, form: { hidden: true } },
    postcode: { list: false, form: { hidden: true } },
    recalled: { list: false, form: { span: 6, section: 'recall' } },
    notRecalledNote: { list: false, form: { span: 6, section: 'recall' } },
    /** Who took the money: the counter receipt's (server-set), printed under ผู้รับเงิน */
    receivedBy: { readOnly: true, list: false },
    /** Issued by the customer from the bill's tracking page (server-set) */
    selfIssued: { readOnly: true, type: 'boolean', filter: true, list: { width: 120, priority: 3 } },
    /** Per bill and party, what the customer paid (server-set) */
    lines: { readOnly: true, type: 'json', list: false, export: false },
    /** The freight paid for: the sum of `lines` (server-set) */
    amount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Withheld by the customer (server-set) */
    whtAmount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** The money received: `amount` less `whtAmount` (server-set) */
    received: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    /** Paid in an earlier month than it was issued: the accounting firm got that month's papers without it (server-set) */
    priorMonth: { readOnly: true, type: 'boolean', filter: true, list: { width: 120, priority: 3 } },
    status: {
      readOnly: true,
      type: 'select',
      options: customerReceiptStatuses,
      filter: true,
      tones: { draft: 'neutral', issued: 'success', cancelled: 'danger' },
      list: { width: 120, priority: 1 },
    },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'number',
  columns: [
    'number',
    'issueDate',
    'name',
    'payerName',
    'paidOn',
    'receiptId',
    'received',
    'status',
    'priorMonth',
    'selfIssued',
    'amount',
    'whtAmount',
  ],
  sections: ['main', 'customer', 'address', 'recall', 'other'],
  defaultSort: '-issueDate',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  // What was issued is what the customer got: a wrong one is cancelled and issued again
  submittable: { cancelReason: 'required', allowOnSubmit: [] },
  card: { title: 'name', subtitle: 'number', meta: ['issueDate', 'received'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/** A refund of the counter receipt, as far as its figures go */
export interface ReceiptRefund {
  kind: RefundKind
  status: RefundStatus
  amount: number
  waybillId?: string | null
  side?: PaySide | null
}

export interface CustomerReceiptFigures {
  lines: Omit<CustomerReceiptLine, 'waybillNumber'>[]
  /** The freight paid for */
  amount: number
  /** Withheld: at the counter, or given back after on a `wht` refund with its 50 ทวิ */
  whtAmount: number
  /** The money that stayed with the company */
  received: number
}

/**
 * What the customer paid on a counter receipt, as the receipt in their name says it: each line less what a paid refund
 * gave back of that bill's part (the price lowered after), and the 1% a paid `wht` refund gave back counted as withheld.
 * Refunds asked for or approved and not yet paid, or cancelled, change nothing (one not yet paid holds the issue back).
 */
export function customerReceiptFigures(
  receipt: { lines: readonly ReceiptLine[]; whtAmount?: number | null },
  refunds: readonly ReceiptRefund[],
): CustomerReceiptFigures {
  const paid = refunds.filter((r) => r.status === 'paid')
  const lines = receipt.lines
    .map((line) => {
      const back = paid
        .filter((r) => r.kind !== 'wht' && r.waybillId === line.waybillId && r.side === line.side)
        .reduce((sum, r) => sum + r.amount, 0)
      return { waybillId: line.waybillId, side: line.side, amount: round2(Math.max(line.amount - back, 0)) }
    })
    .filter((line) => line.amount > 0)
  const amount = round2(lines.reduce((sum, line) => sum + line.amount, 0))
  const taxBack = paid.filter((r) => r.kind === 'wht').reduce((sum, r) => sum + r.amount, 0)
  const whtAmount = round2((Number(receipt.whtAmount) || 0) + taxBack)
  return { lines, amount, whtAmount, received: round2(amount - whtAmount) }
}

/** A counter receipt as the receipt in the customer's name starts from it: who paid, and their details if it has them */
export interface CounterPayer {
  payerName?: string | null
  payerTaxId?: string | null
  payerBranch?: string | null
  payerAddress?: string | null
  payerSubdistrict?: string | null
  payerDistrict?: string | null
  payerProvince?: string | null
  payerPostcode?: string | null
}

/**
 * Who a new receipt in the customer's name is made out to: as the counter receipt has it when it was made out to a
 * company there (checked at the counter), then the tax ID of a 50 ทวิ given back on a refund after, then who paid
 * (`payerDefaults`: the contact's name for receipts before the name the bill wrote)
 */
export const customerReceiptDefaults = (counter: CounterPayer, parties: readonly PayingParty[], refundTaxId?: string | null) =>
  payerDefaults(counter.payerName, parties, {
    name: counter.payerTaxId ? counter.payerName : null,
    taxId: counter.payerTaxId || refundTaxId,
    branch: counter.payerBranch,
    address: counter.payerAddress,
    subdistrict: counter.payerSubdistrict,
    district: counter.payerDistrict,
    province: counter.payerProvince,
    postcode: counter.payerPostcode,
  })

const squash = (name: unknown) =>
  String(name ?? '')
    .replace(/\s+/g, ' ')
    .trim()

/** Paid in an earlier month than it is issued */
export const paidPriorMonth = (paidOn: string, issueDate: string) => dayjs(paidOn).format('YYYY-MM') < dayjs(issueDate).format('YYYY-MM')

/** Asked for more than 6 months after the money came: still issued, with a warning to check who asks */
export const lateRequest = (paidOn: string, on: string) => dayjs(paidOn).add(6, 'month').isBefore(dayjs(on), 'day')

/**
 * Why it cannot be issued as it stands, field by field (the API refuses with these, 422). A person's tax ID and branch
 * are never kept (the API clears them), so they are no problem
 */
export function customerReceiptProblems(values: {
  partyType?: string | null
  name?: string | null
  recalled?: boolean | null
  notRecalledNote?: string | null
}): { field: string; message: string }[] {
  const problems: { field: string; message: string }[] = []
  if (!values.partyType) problems.push({ field: 'partyType', message: 'เลือกว่าลูกค้าเป็นนิติบุคคลหรือบุคคลธรรมดา' })
  if (!squash(values.name)) problems.push({ field: 'name', message: 'ใส่ชื่อลูกค้า' })
  if (!values.recalled && !squash(values.notRecalledNote)) {
    problems.push({ field: 'notRecalledNote', message: 'ติ๊กว่าเรียกใบเสร็จเดิมคืนแล้ว หรือใส่เหตุที่เรียกคืนไม่ได้' })
  }
  return problems
}

/** Days after the money came that its payer may issue a receipt in their name themselves, from the bill's tracking page */
export const SELF_RECEIPT_DAYS = 30

/** The last day the payer may issue it themselves */
export const selfReceiptUntil = (paidOn: string) => dayjs(paidOn).add(SELF_RECEIPT_DAYS, 'day').format('YYYY-MM-DD')

/**
 * Whether a counter receipt's payer may issue the receipt in their name themselves, from the tracking page of a bill it
 * paid: `issued` once one stands for it, `expired` after `selfReceiptUntil`, `office` when the branch or accounting
 * issues it — the counter receipt took the money of more than one payer (their bills would show on it), or a refund of
 * it waits to be paid — and `open` otherwise
 */
export type SelfReceiptState = 'open' | 'issued' | 'expired' | 'office'

export function selfReceiptState(input: {
  /** A receipt in the customer's name stands for it */
  replaced: boolean
  paidOn: string
  today: string
  /** The paying parties of its bills (`payingParties`) */
  payers: number
  refundWaiting: boolean
}): SelfReceiptState {
  if (input.replaced) return 'issued'
  if (input.today > selfReceiptUntil(input.paidOn)) return 'expired'
  if (input.payers !== 1 || input.refundWaiting) return 'office'
  return 'open'
}

const required = (message: string, max: number) => z.string().trim().min(1, message).max(max)

/**
 * What the payer fills in to issue it themselves: a company's (นิติบุคคล) only — its name, a tax ID whose last digit
 * checks, head office or a branch, and its address. A person asks accounting, as before
 */
export const selfReceiptInput = z.object({
  name: required('ใส่ชื่อนิติบุคคล', 200),
  taxId: z.string().trim().pipe(juristicTaxIdSchema).refine(thaiIdValid, 'เลขผู้เสียภาษีไม่ถูกต้อง ตรวจอีกครั้ง'),
  /** 00000 = head office, otherwise the branch's number */
  branch: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'สาขาเป็นตัวเลข 5 หลัก สำนักงานใหญ่คือ 00000'),
  address: required('ใส่ที่อยู่ (เลขที่ อาคาร ถนน)', 500),
  subdistrict: required('เลือกตำบล อำเภอ จังหวัด', 100),
  district: required('เลือกตำบล อำเภอ จังหวัด', 100),
  province: required('เลือกตำบล อำเภอ จังหวัด', 100),
  postcode: required('เลือกตำบล อำเภอ จังหวัด', 10),
})
export type SelfReceiptInput = z.infer<typeof selfReceiptInput>

/** What a page with no account shows of a receipt in a company's name: what it prints, without the notes kept inside */
export const publicCustomerReceiptFields = [
  'id',
  'number',
  'issueDate',
  'paidOn',
  'receiptId',
  'partyType',
  'name',
  'taxId',
  'branch',
  'address',
  'subdistrict',
  'district',
  'province',
  'postcode',
  'lines',
  'amount',
  'whtAmount',
  'received',
  'receivedBy',
  'status',
  'docstatus',
] as const

export const publicCustomerReceiptOf = (record: Record<string, unknown>) =>
  Object.fromEntries(publicCustomerReceiptFields.map((field) => [field, record[field] ?? null]))
